import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink } from "react-router-dom";
import { COMPANY } from "../data/company";
import { imageUrl } from "../lib/images";

export default function MobileMenu({ open, onClose, triggerRef }) {
  const panelRef = useRef(null);
  const [servicesOpen, setServicesOpen] = useState(true);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const siblings = [...document.body.children].filter((node) => node !== panelRef.current && node.tagName !== "SCRIPT");
    const previousInert = siblings.map((node) => node.inert);
    siblings.forEach((node) => { node.inert = true; });
    const focusTimer = window.setTimeout(() => panelRef.current?.querySelector("button")?.focus({ preventScroll: true }), 50);
    const handleKey = (event) => {
      if (event.key === "Escape") onClose();
      if (event.key !== "Tab") return;
      const controls = [...panelRef.current.querySelectorAll('a[href], button')].filter((node) => node.getClientRects().length && !node.closest('[inert]'));
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    const media = window.matchMedia("(min-width: 1200px)");
    const handleResize = () => { if (media.matches) onClose(); };
    window.addEventListener("keydown", handleKey);
    media.addEventListener("change", handleResize);
    return () => {
      window.clearTimeout(focusTimer);
      document.body.style.overflow = previousOverflow;
      siblings.forEach((node, index) => { node.inert = previousInert[index]; });
      window.removeEventListener("keydown", handleKey);
      media.removeEventListener("change", handleResize);
      triggerRef.current?.focus({ preventScroll: true });
    };
  }, [open, onClose, triggerRef]);

  const linkClass = ({ isActive }) => `group flex items-center justify-between py-3 text-2xl sm:text-3xl font-bold tracking-tight transition-colors focus-visible:outline-2 focus-visible:outline-[#A78E51] ${isActive ? "text-[#A78E51]" : "text-[#111111] hover:text-[#A78E51]"}`;

  return createPortal(
    <div
      ref={panelRef}
      id="mobile-navigation"
      role="dialog"
      aria-modal={open ? "true" : undefined}
      aria-label="Mobilmenu"
      aria-hidden={!open}
      inert={!open}
      className={`fixed inset-0 z-[100] flex h-dvh flex-col overflow-hidden bg-[#FAFAF8] transition-[opacity,transform,visibility] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none min-[1200px]:hidden ${open ? "visible translate-y-0 opacity-100" : "invisible -translate-y-5 opacity-0 pointer-events-none"}`}
    >
      <div className="flex shrink-0 items-center justify-between border-b border-[#E5E5E0] px-6 py-5 sm:px-10 sm:py-7">
        <Link to="/" onClick={onClose} className="flex items-center gap-3.5 group focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A78E51] rounded-md py-1">
          <img src={imageUrl("/_assets/ag-monogram-med-sort-tagkontur.png", "width=96,quality=80,format=auto")} alt="Tømrermester Andreas Guldmann logo" className="w-11 h-11 object-contain" />
          <div className="flex flex-col">
            <span className="font-bold text-base sm:text-lg leading-tight tracking-tight text-[#111111] transition-colors">Tømrermester</span>
            <span className="font-bold text-base sm:text-lg leading-tight tracking-tight text-[#A78E51]">Andreas Guldmann</span>
          </div>
        </Link>
        <button type="button" onClick={onClose} aria-label="Luk menu" className="group flex size-12 items-center justify-center rounded-full border border-[#D8D5CC] text-[#111111] transition-colors hover:border-[#A78E51] hover:bg-[#A78E51]/10 focus-visible:outline-2 focus-visible:outline-[#A78E51]">
          <i className="fa-light fa-xmark text-2xl transition-transform duration-300 group-hover:rotate-90 motion-reduce:transition-none" aria-hidden="true" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto overscroll-contain px-6 pb-8 pt-5 sm:px-10 sm:py-10">
        <div className={`mx-auto grid min-h-full max-w-5xl gap-8 sm:gap-12 md:grid-cols-2 md:items-center transition-[opacity,transform] duration-500 motion-reduce:transition-none ${open ? "translate-y-0 opacity-100 delay-100" : "translate-y-6 opacity-0"}`}>
          <nav aria-label="Mobil navigation" onClick={(event) => { if (event.target.closest("a")) onClose(); }}>
            <NavLink to="/" end className={linkClass}>Forside<i className="fa-light fa-arrow-up-right text-base text-[#A78E51]" aria-hidden="true" /></NavLink>
            <div className="border-y border-[#E5E5E0]">
              <div className="flex items-center justify-between">
                <NavLink to="/ydelser" className={linkClass}>Ydelser</NavLink>
                <button type="button" aria-label="Fold ydelser ud eller ind" aria-expanded={servicesOpen} aria-controls="mobile-service-links" onClick={() => setServicesOpen((value) => !value)} className="flex size-11 items-center justify-center rounded-full text-[#A78E51] hover:bg-[#A78E51]/10 focus-visible:outline-2 focus-visible:outline-[#A78E51]">
                  <i className={`fa-light fa-plus text-lg transition-transform duration-300 motion-reduce:transition-none ${servicesOpen ? "rotate-45" : ""}`} aria-hidden="true" />
                </button>
              </div>
              <div id="mobile-service-links" inert={!servicesOpen} aria-hidden={!servicesOpen} className={`grid transition-[grid-template-rows,opacity] duration-300 motion-reduce:transition-none ${servicesOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                <div className="overflow-hidden">
                  <div className="pb-3">
                    {COMPANY.services.map((service) => <NavLink key={service.slug} to={service.path} className={({ isActive }) => `flex items-center gap-3 py-2 text-sm sm:text-base font-medium transition-colors hover:text-[#A78E51] ${isActive ? "text-[#A78E51]" : "text-neutral-600"}`}><i className={`${service.icon} w-5 text-[#A78E51]`} aria-hidden="true" />{service.title}</NavLink>)}
                  </div>
                </div>
              </div>
            </div>
            <NavLink to="/galleri" className={linkClass}>Galleri<i className="fa-light fa-arrow-up-right text-base text-[#A78E51]" aria-hidden="true" /></NavLink>
            <NavLink to="/om-andreas" className={linkClass}>Om Andreas<i className="fa-light fa-arrow-up-right text-base text-[#A78E51]" aria-hidden="true" /></NavLink>
            <NavLink to="/kontakt" className={linkClass}>Kontakt<i className="fa-light fa-arrow-up-right text-base text-[#A78E51]" aria-hidden="true" /></NavLink>
          </nav>

          <div className="relative overflow-hidden rounded-xl bg-[#111111] p-6 sm:p-8 text-white">
            <div className="absolute -right-12 -top-12 size-40 rounded-full border border-[#A78E51]/20" aria-hidden="true" />
            <p className="relative text-xs font-bold uppercase tracking-[0.18em] text-[#A78E51]">Kontakt Andreas</p>
            <a href={COMPANY.phoneLink} className="relative mt-6 flex items-center gap-3 text-3xl sm:text-4xl font-bold tracking-tight hover:text-[#A78E51] transition-colors"><i className="fa-light fa-phone text-xl text-[#A78E51]" aria-hidden="true" />{COMPANY.phone}</a>
            <div className="relative mt-7 flex flex-col gap-3">
              <a href={COMPANY.phoneLink} className="flex items-center justify-between rounded-md bg-[#A78E51] px-5 py-4 text-sm font-bold text-white transition-colors hover:bg-[#937B43]">Ring til Andreas<i className="fa-solid fa-phone" aria-hidden="true" /></a>
              <Link to="/kontakt#kontaktformular" onClick={onClose} className="flex items-center justify-between rounded-md border border-white bg-white px-5 py-4 text-sm font-bold text-[#111111] transition-colors hover:border-neutral-200 hover:bg-neutral-100">Skriv til andreas<i className="fa-light fa-arrow-right" aria-hidden="true" /></Link>
            </div>
          </div>
        </div>
      </div>
    </div>, document.body,
  );
}
